"use client";

import type React from "react";

import { VerificationCodeForm } from "@/components/auth/VerificationCodeForm";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ModeToggle } from "@/components/ui/mode-toggle";
import { useSignIn, useUser } from "@clerk/clerk-react";
import { CheckCircle, Mail, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface FormData {
  email: string;
  otpCode: string;
  rememberMe: boolean;
}

interface FormErrors {
  email?: string;
  otpCode?: string;
  general?: string;
}

export default function SignInPage() {
  const navigate = useNavigate();
  const { signIn, setActive } = useSignIn();
  const { isSignedIn } = useUser();

  const [formData, setFormData] = useState<FormData>({
    email: "",
    otpCode: "",
    rememberMe: false,
  });
  const [errors, setErrors] = useState<FormErrors>({
    general: "",
    email: "",
    otpCode: "",
  });
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Redirect to dashboard if user is already signed in
  useEffect(() => {
    if (isSignedIn) {
      navigate("/dashboard");
    }
  }, [isSignedIn, navigate]);

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Real-time validation
  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case "email":
        if (!value) return "Email is required";
        if (!emailRegex.test(value))
          return "Please enter a valid email address";
        return undefined;
      case "otpCode":
        if (!value) return "OTP code is required";
        if (value.length !== 6) return "OTP code must be 6 digits";
        if (!/^\d{6}$/.test(value)) return "OTP code must contain only numbers";
        return undefined;
      default:
        return undefined;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({ ...prev, [name]: fieldValue }));

    // Clear general error when user starts typing
    if (errors.general) {
      setErrors((prev) => ({ ...prev, general: undefined }));
    }

    // Validate field in real-time if it has been touched
    if (touched[name] && type !== "checkbox") {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    newErrors.email = validateField("email", formData.email);
    if (isOtpSent) {
      newErrors.otpCode = validateField("otpCode", formData.otpCode);
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== undefined);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mark all fields as touched
    setTouched({ email: true, otpCode: isOtpSent });

    if (validateForm()) {
      try {
        if (!signIn) {
          setIsSubmitting(false);
          return;
        }

        if (!isOtpSent) {
          // Step 1: Send OTP to email
          const signInAttempt = await signIn.create({
            identifier: formData.email,
          });

          // Prepare for email code verification
          const emailCodeFactor = signInAttempt.supportedFirstFactors?.find(
            (factor) => factor.strategy === "email_code",
          );

          if (!emailCodeFactor?.emailAddressId) {
            throw new Error(
              "Email code authentication is not supported for this account",
            );
          }

          await signInAttempt.prepareFirstFactor({
            strategy: "email_code",
            emailAddressId: emailCodeFactor.emailAddressId,
          });

          setIsOtpSent(true);
          setErrors({ general: "" });
        } else {
          // Step 2: Verify OTP code
          const signInAttempt = await signIn.attemptFirstFactor({
            strategy: "email_code",
            code: formData.otpCode,
          });

          if (signInAttempt.status === "complete") {
            // Sign in was successful
            if (setActive) {
              await setActive({ session: signInAttempt.createdSessionId });
            }
            navigate("/dashboard");
          } else {
            // Handle other statuses
            setErrors({
              general: "Sign in requires additional verification.",
            });
          }
        }
      } catch (err: unknown) {
        // Handle Clerk errors
        const clerkError = err as { errors?: { message: string }[] };
        const errorMessage =
          clerkError?.errors?.[0]?.message ||
          (isOtpSent
            ? "Invalid OTP code. Please try again."
            : "Failed to send OTP. Please check your email address.");
        setErrors({ general: errorMessage });
      }
    }

    setIsSubmitting(false);
  };

  const getFieldStatus = (fieldName: keyof FormErrors) => {
    if (!touched[fieldName]) return null;
    return errors[fieldName] ? "error" : "success";
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-12 sm:px-6 lg:px-8 relative">
      {/* Floating Theme Toggle Button */}
      <div className="fixed bottom-4 left-4 z-50">
        <ModeToggle />
      </div>

      <div className="w-full max-w-md space-y-8">
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="dark:text-white">Sign In</CardTitle>
            <CardDescription className="dark:text-gray-400">
              {isOtpSent
                ? "Enter the 6-digit code sent to your email"
                : "Enter your email to receive a verification code"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* General Error */}
              {errors.general && (
                <Alert variant="destructive">
                  <AlertDescription>{errors.general}</AlertDescription>
                </Alert>
              )}

              {/* Success message when OTP is sent */}
              {isOtpSent && !errors.general && (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    Verification code sent to {formData.email}.
                  </AlertDescription>
                </Alert>
              )}

              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email" className="dark:text-gray-200">
                  Email address
                </Label>
                <div className="relative">
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    disabled={isOtpSent}
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Enter your email"
                    className={`pl-10 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 ${
                      getFieldStatus("email") === "error"
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500 dark:border-red-400 dark:focus:border-red-400"
                        : "border-green-500 focus:border-green-500 focus:ring-green-500 dark:border-green-400 dark:focus:border-green-400"
                    }`}
                  />
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  {getFieldStatus("email") === "success" && (
                    <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
                  )}
                  {getFieldStatus("email") === "error" && (
                    <XCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-red-500" />
                  )}
                </div>
                {errors.email && (
                  <Alert variant="destructive">
                    <AlertDescription className="text-sm">
                      {errors.email}
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {/* OTP Code Field - Only show when OTP is sent */}
              {isOtpSent && (
                <VerificationCodeForm
                  value={formData.otpCode}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, otpCode: value }))
                  }
                  onBlur={handleBlur}
                  error={errors.otpCode}
                  touched={touched.otpCode}
                />
              )}
              {/* Clerk CAPTCHA widget placeholder */}
              <div
                id="clerk-captcha"
                data-cl-theme="auto"
                data-cl-size="normal"
              />

              <Button
                type="submit"
                className="w-full mb-3"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {isOtpSent ? "Verifying..." : "Sending code..."}
                  </div>
                ) : isOtpSent ? (
                  "Verify Code"
                ) : (
                  "Send Verification Code"
                )}
              </Button>

              {/* Back to email step button */}
              {isOtpSent && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setIsOtpSent(false);
                    setFormData((prev) => ({ ...prev, otpCode: "" }));
                    setErrors({ general: "", email: "", otpCode: "" });
                    setTouched({});
                  }}
                >
                  Use Different Email
                </Button>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
